import { FC, useEffect, useMemo, useState } from 'react';
import { Button, Icon, IconButton } from '@kontentino/ui';
import { faArrowsRotate } from '@fortawesome/pro-regular-svg-icons/faArrowsRotate';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons/faTriangleExclamation';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import { Trans, useTranslation } from 'react-i18next';
import clsx from 'clsx';

interface AnalyticsSyncBackgroundStripProps {
  targetLabel: string;
  completed: number;
  total: number;
  isCompleted: boolean;
  isError?: boolean;
  errorText?: string;
  targetIsGroup: boolean;
  syncStartedAt: number | null;
  showJump?: boolean;
  onJump?: () => void;
  onCancel?: () => void;
  onClose?: () => void;
}

const AnalyticsSyncBackgroundStrip: FC<AnalyticsSyncBackgroundStripProps> = ({
  targetLabel,
  completed,
  total,
  isCompleted,
  isError = false,
  errorText,
  targetIsGroup,
  syncStartedAt,
  showJump = false,
  onJump,
  onCancel,
  onClose,
}) => {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => Date.now());

  const isActive = !isCompleted && !isError;

  useEffect(() => {
    if (!isActive) {
      return;
    }
    if (
      syncStartedAt == null ||
      completed === 0 ||
      completed >= total ||
      total <= 1
    ) {
      return;
    }
    const intervalId = window.setInterval(() => {
      setNow(Date.now());
    }, 10_000);
    return () => window.clearInterval(intervalId);
  }, [isActive, syncStartedAt, completed, total]);

  useEffect(() => {
    setNow(Date.now());
  }, [completed, total, syncStartedAt, isCompleted, isError]);

  const remainingHint = useMemo(() => {
    if (
      !isActive ||
      syncStartedAt == null ||
      completed === 0 ||
      completed >= total ||
      total <= 1
    ) {
      return null;
    }
    const elapsedMs = Math.max(now - syncStartedAt, 0);
    if (elapsedMs < 2500) {
      return null;
    }
    const msPerCompleted = elapsedMs / completed;
    const remainingMs = msPerCompleted * (total - completed);
    if (remainingMs < 55_000) {
      return t('analyticsSyncBackgroundStripRemainingLessThanMinute');
    }
    const mins = Math.max(1, Math.round(remainingMs / 60_000));
    return t('analyticsSyncBackgroundStripRemainingAboutMinute', {
      count: mins,
    });
  }, [completed, isActive, now, syncStartedAt, t, total]);

  const scopeLabel = targetIsGroup ? t('group') : t('profile');
  const metaText = useMemo(() => {
    if (!isActive) {
      return '';
    }
    const segments: string[] = [];
    if (targetIsGroup && total > 1) {
      segments.push(`${completed}/${total}`);
      segments.push(scopeLabel);
    }
    if (remainingHint != null) {
      segments.push(remainingHint);
    }
    return segments.join(' · ');
  }, [completed, isActive, remainingHint, scopeLabel, targetIsGroup, total]);

  const strongCls = 'tw-font-semibold tw-text-grayscale-180';

  return (
    <div
      className={clsx(
        'tw-relative tw-mb-3 tw-overflow-hidden tw-rounded-lg tw-border',
        {
          'tw-border-danger-20 tw-bg-danger-10': isError,
          'tw-border-success-20 tw-bg-success-10': !isError && isCompleted,
          'tw-border-grayscale-20 tw-bg-white': isActive,
        },
      )}
      data-cy="analytics-sync-background-strip"
      role={isError ? 'alert' : isActive ? 'status' : undefined}
      aria-live={isError ? 'assertive' : isActive ? 'polite' : undefined}
    >
      {isActive && (
        <div
          className="tw-pointer-events-none tw-absolute tw-inset-0 tw-z-0 tw-analytics-sync-strip-shimmer"
          aria-hidden
        />
      )}
      <div className="tw-relative tw-z-[1] tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-2">
        <span
          className={clsx(
            'tw-min-h-7 tw-min-w-7 tw-flex tw-h-7 tw-w-7 tw-shrink-0 tw-items-center tw-justify-center tw-text-sm',
            {
              'tw-text-danger-100': isError,
              'tw-text-success-100': !isError && isCompleted,
              'tw-text-primary-100': isActive,
            },
          )}
          aria-hidden
        >
          {isError ? (
            <Icon icon={faTriangleExclamation} size={12} />
          ) : isCompleted ? (
            <Icon icon={faCheck} size={12} />
          ) : (
            <Icon icon={faArrowsRotate} size={12} className="tw-animate-spin" />
          )}
        </span>

        {isError ? (
          <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-x-2">
            <span className="tw-text-sm tw-text-grayscale-180">
              {errorText ?? t('analyticsSyncFailed')}
            </span>
          </div>
        ) : isActive ? (
          <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1">
            <span className="tw-text-sm tw-text-grayscale-180">
              <Trans
                i18nKey="analyticsSyncBackgroundStripSyncingRich"
                components={{ 1: <strong className={strongCls} /> }}
                values={{
                  prefix: t('analyticsSyncBackgroundStripPrefixSyncing'),
                  target: targetLabel,
                }}
              />
            </span>
            {metaText.trim().length > 0 ? (
              <>
                <span
                  className="tw-select-none tw-text-sm tw-font-medium tw-leading-6 tw-text-grayscale-50"
                  aria-hidden
                >
                  ·
                </span>
                <span className="tw-text-[13px] tw-font-medium tw-leading-6 tw-text-grayscale-100">
                  {metaText}
                </span>
              </>
            ) : null}
            <span className="tw-font-light tw-basis-full tw-text-xs tw-leading-4 tw-text-grayscale-100">
              {t('analyticsSyncBackgroundStripLeavePageHint')}
            </span>
          </div>
        ) : (
          <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-x-2">
            <span className="tw-text-sm tw-text-grayscale-180">
              <Trans
                i18nKey="analyticsSyncBackgroundStripCompleteRich"
                components={{ 1: <strong className={strongCls} /> }}
                values={{ target: targetLabel }}
              />
            </span>
          </div>
        )}

        {showJump && onJump ? (
          <Button
            type="button"
            variant="secondary"
            size="small"
            className="tw-shrink-0"
            onClick={onJump}
            data-cy="analytics-sync-background-strip_jump"
          >
            {t('analyticsSyncToastJumpToTarget')}
          </Button>
        ) : null}

        {isActive && onCancel ? (
          <IconButton
            type="button"
            variant="ghost"
            size="small"
            icon={<Icon icon={faXmark} size={12} />}
            className="tw-min-h-7 tw-min-w-7 tw-h-7 tw-w-7 tw-shrink-0 tw-rounded-md tw-border tw-border-primary-100 tw-bg-white tw-p-0 tw-text-primary-100 hover:tw-bg-primary-10"
            aria-label={t('analyticsSyncBackgroundStripCancel')}
            onClick={onCancel}
            data-cy="analytics-sync-background-strip_cancel"
          />
        ) : null}

        {isError && onClose ? (
          <IconButton
            type="button"
            variant="ghost"
            size="small"
            icon={<Icon icon={faXmark} size={12} />}
            className="tw-min-h-7 tw-min-w-7 tw-h-7 tw-w-7 tw-shrink-0 tw-rounded-md tw-border tw-border-danger-100 tw-bg-white tw-p-0 tw-text-danger-100 hover:tw-bg-danger-10"
            aria-label={t('close')}
            onClick={onClose}
            data-cy="analytics-sync-background-strip_dismiss-error"
          />
        ) : null}
      </div>
    </div>
  );
};

export default AnalyticsSyncBackgroundStrip;
